import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { Calculate } from '@mui/icons-material';
import { DrugSubstanceDosesProvider } from '../../contexts/api-contexts/DrugSubstanceDosesContext';
import { DrugDosageCalculatorWithInfo } from './DrugDosageCalculatorWithInfo';
import {
  CalculationType,
  CalculationUnit,
  CalculatedValues,
} from '../../types/DrugDosageCalculator.type';
import {
  DoseAlternative,
  DrugSubstanceDose,
} from '../../types/DrugSubstanceDose.type';

export interface DrugDosageCalculatorProps {
  /** Drug substance ID */
  drugSubstanceId: number;
  /** Drug substance name for display */
  drugSubstanceName?: string;
  /** Animal type name for display */
  animalTypeName?: string;
  /** Disease name for context */
  diseaseName?: string;
  /** Patient weight in kg */
  patientWeightKg?: number;
  /** Whether weight field is disabled */
  weightDisabled?: boolean;
  /** Callback when calculation is performed */
  onCalculation?: (
    calculationType: CalculationType,
    values: CalculatedValues<CalculationUnit>,
    selectedAlternative?: DoseAlternative,
    selectedDose?: DrugSubstanceDose
  ) => void;
  /** Whether to show the header */
  showHeader?: boolean;
}

/**
 * Standalone Drug Dosage Calculator component with full dosage information.
 * Can be embedded directly in a page without a dialog wrapper.
 * Includes the DrugSubstanceDosesProvider internally so it can be used standalone.
 */
export const DrugDosageCalculator: React.FC<DrugDosageCalculatorProps> = ({
  drugSubstanceId,
  drugSubstanceName,
  animalTypeName,
  diseaseName,
  patientWeightKg,
  weightDisabled = false,
  onCalculation,
  showHeader = true,
}) => {
  return (
    <DrugSubstanceDosesProvider>
      <Paper elevation={0} sx={{ p: 3 }}>
        {showHeader && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Calculate sx={{ color: 'secondary.main' }} />
            <Typography variant="h6" component="div">
              Drug Dosage Calculator
            </Typography>
          </Box>
        )}

        <DrugDosageCalculatorWithInfo
          drugSubstanceId={drugSubstanceId}
          drugSubstanceName={drugSubstanceName}
          animalTypeName={animalTypeName}
          diseaseName={diseaseName}
          patientWeightKg={patientWeightKg}
          weightDisabled={weightDisabled}
          onCalculation={onCalculation}
        />
      </Paper>
    </DrugSubstanceDosesProvider>
  );
};

export default DrugDosageCalculator;
